import { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Button } from '@design-system/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system/dialog';
import { cn } from '@design-system/helpers';
import FilePreview from '@design-system/molecules/file-preview';
import { Text } from '@design-system/text';
import cloudIcon from '@shared/assets/svgs/cloud-icon.svg';
import { showToast } from '@src/shared/utils/helpers/show-toast';

import {
  DOCUMENT_EXTENSIONS,
  DOCUMENT_MAX_MB,
  knowledgeDocumentsApi,
} from '../services/knowledge-content-api';

const ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'text/markdown': ['.md'],
  'text/plain': ['.txt'],
  'text/html': ['.html', '.htm'],
};

const FORMATS_LABEL = DOCUMENT_EXTENSIONS.map((ext) =>
  ext.slice(1).toUpperCase()
)
  .filter((label, index, all) => all.indexOf(label) === index)
  .join(', ');

type Picked = { key: string; file: File; error?: string };

function keyOf(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Pick one or more files and upload them into the base one at a time. Each
 * upload is its own request, so one rejected file (a duplicate name, an
 * unsupported type the dropzone let through) never blocks the others: it
 * stays in the list with its error and the rest land.
 */
export function AddFilesDialog({
  kbaseId,
  open,
  onOpenChange,
}: {
  kbaseId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const upload = knowledgeDocumentsApi.useUpload();

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      multiple: true,
      accept: ACCEPT,
      maxSize: DOCUMENT_MAX_MB * 1024 * 1024,
      onDrop: (accepted) =>
        setPicked((current) => {
          const known = new Set(current.map((item) => item.key));
          const fresh = accepted
            .filter((file) => !known.has(keyOf(file)))
            .map((file) => ({ key: keyOf(file), file }));
          return [...current, ...fresh];
        }),
    });

  const reset = () => {
    setPicked([]);
    setProgress(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const confirm = async () => {
    const queue = picked;
    const failed: Picked[] = [];
    setProgress({ done: 0, total: queue.length });
    for (const [index, item] of queue.entries()) {
      try {
        await upload.mutateAsync({ kbase: kbaseId, file: item.file });
      } catch (error) {
        failed.push({
          ...item,
          error: typeof error === 'string' ? error : 'Upload failed',
        });
      }
      setProgress({ done: index + 1, total: queue.length });
    }
    const uploaded = queue.length - failed.length;
    if (uploaded > 0) {
      showToast({
        title:
          uploaded === 1 ? '1 file uploaded' : `${uploaded} files uploaded`,
        description:
          'Indexing runs in the background; the status icon reports when each file is ready.',
        variant: 'success',
      });
    }
    if (failed.length === 0) {
      close();
      return;
    }
    setPicked(failed);
    setProgress(null);
  };

  const uploading = progress !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent
        align='right'
        className='max-w-[600px] rounded-2xl'
      >
        <DialogHeader className='p-4'>
          <DialogTitle>Add Files</DialogTitle>
          <p className='mt-1 text-sm font-normal text-gray-600'>
            Train Your Model On Files
          </p>
        </DialogHeader>
        <DialogBody className='flex flex-col gap-3 p-4'>
          <div
            {...getRootProps()}
            data-slot='files-dropzone'
            className={cn(
              'cursor-pointer rounded-xl border-2 border-dashed bg-primary-30 p-8 text-center transition-colors',
              isDragActive ? 'border-primary-600' : 'border-primary-200'
            )}
          >
            <input
              {...getInputProps()}
              aria-label='Upload files'
              disabled={uploading}
            />
            <img
              alt=''
              src={cloudIcon}
              className='mx-auto !h-10 !w-10'
            />
            <Text className='py-2 text-sm font-medium text-gray-800'>
              Click to upload{' '}
              <Text
                variant='span'
                className='font-normal'
              >
                or drag and drop
              </Text>
            </Text>
            <Text className='text-xs text-gray-600'>
              {FORMATS_LABEL}. Max {DOCUMENT_MAX_MB}MB
            </Text>
          </div>
          {fileRejections.length > 0 && (
            <p
              role='alert'
              className='rounded-[10px] bg-red-100 px-3 py-1.5 text-xs text-red-500'
            >
              {fileRejections.length === 1
                ? `${fileRejections[0].file.name} was skipped: ${fileRejections[0].errors[0]?.message}`
                : `${fileRejections.length} files were skipped: unsupported type or over ${DOCUMENT_MAX_MB}MB.`}
            </p>
          )}
          <ul
            aria-label='Files to upload'
            className='flex flex-col gap-3'
          >
            {picked.map((item) => (
              <li
                key={item.key}
                data-slot='picked-file'
              >
                <FilePreview
                  file={item.file}
                  showDelete={!uploading}
                  onDelete={() =>
                    setPicked((current) =>
                      current.filter((entry) => entry.key !== item.key)
                    )
                  }
                  onRename={(name) =>
                    setPicked((current) =>
                      current.map((entry) =>
                        entry.key === item.key
                          ? {
                              ...entry,
                              file: new File([entry.file], name, {
                                type: entry.file.type,
                                lastModified: entry.file.lastModified,
                              }),
                            }
                          : entry
                      )
                    )
                  }
                />
                {item.error && (
                  <p
                    role='alert'
                    className='mt-1 text-xs text-red-500'
                  >
                    {item.error}
                  </p>
                )}
              </li>
            ))}
          </ul>
          {uploading && (
            <p
              role='status'
              className='text-sm text-gray-600'
            >
              Uploading {Math.min(progress.done + 1, progress.total)} of{' '}
              {progress.total}…
            </p>
          )}
        </DialogBody>
        <DialogFooter className='p-4'>
          <Button
            type='button'
            variant='primary'
            className='flex-1'
            disabled={picked.length === 0 || uploading}
            isLoading={uploading}
            onClick={confirm}
          >
            Confirm
          </Button>
          <DialogClose asChild>
            <Button
              type='button'
              variant='primaryOutline'
              className='flex-1 border-primary-200 text-primary-1000'
              disabled={uploading}
            >
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
